import type {LoadingStates} from '@Utils/loading.util';
import type {MailingListVO} from '@Components/email-marketing-campaign-audience/email-marketing-campaign-audience.types';

export type EmailCampaignId = string;
export type UserImageHashedFileName = string;
export type AllCampaigns = Record<EmailCampaignId, EmailCampaign>;

export interface EmailCampaignsBaseProps {
  campaignViewType?: EmailMarketingCampaignPageType;
}

export interface EmailGridItemBase {
  isLoading: boolean;
}

export interface EmailCampaignsContainerQueryResult {
  isExtraSmall: boolean;
  isSmall: boolean;
  isMedium: boolean;
  isLarge: boolean;
}

export enum EmailCampaignState {
  DRAFT = 'draft',
  READY = 'ready',
  SENDING = 'sending',
  BATCH_READY = 'batch_ready',
  BATCH_PAUSED = 'batch_paused',
  UNDER_REVIEW = 'under_review',
  UNDER_SUSPICION = 'under_suspicion',
  WAITING_FOR_RETRY = 'waiting',
  SENT = 'sent',
  SUSPENDED = 'suspended',
  PREPARING_GRAPHIC = 'preparing_graphic',
  SPAM_CONTENT = 'spam_content',
  ADMIN_ARCHIVED = 'admin_archived',
  VALIDATING_MAILING_LIST = 'validating_list',
  GENERATION_FAILED = 'generation_failed',
  GENERATION_FAILED_RETRIES = 'failed_retries',
  FAKE_SENT = 'fake_sent',
  FAKE_SENDING = 'fake_sending',
}

export enum EmailMarketingCampaignPageType {
  OWN = 'mine',
  SHARED_WITH_ME = 'shared',
  DELETED = 'trash',
  EVENT_PROMOTION = 'event-promotion',
}

export type MailingListNames = string[];

export interface EmailCampaign {
  id: EmailCampaignId;
  name: string;
  subject: string;
  preheader: string;
  publishOn: number;
  mailingListNames: MailingListNames;
  state: EmailCampaignState;
  isScheduled: boolean;
  isDeleted: boolean;
  isPaused: boolean;
  isEditable: boolean;
  isVideo: boolean;
  lastModified: number;
  imagePreviewURL?: string;
  emailTemplateId?: string;
  posterId?: string;
  sharedBy?: string;
  isRenaming?: boolean;
  reputation?: EmailCampaignReputation;
  useOptimalSendTime?: boolean;
  mailingLists: MailingListVO[];
  isSharedInTeam: boolean;
  userId: number;
}

export interface EmailPreviewPollServerResponse {
  emailTemplateId: string;
  imagePreview: string;
}

export interface EmailPreviewPollFinishResponse {
  isAvailable: boolean;
  data: string;
}

export enum EmailCampaignStatus {
  DRAFT = 'draft',
  SCHEDULED = 'scheduled',
  SENDING = 'sending',
  PUBLISHED = 'published',
  ERROR = 'error',
}

export enum EmailCampaignSorting {
  LAST_MODIFIED = 'last_modified',
  PUBLISHING_DATE = 'publishing_date',
}

export interface EmailCampaignsLazyData {
  ids: string[];
  loadingState: LoadingStates;
  loadMore: boolean;
}

export type EmailCampaignStatusFilter = Record<EmailCampaignStatus, boolean>;
export type EmailCampaignSortingFilter = EmailCampaignSorting;

export interface EmailMarketingCampaignsReduxState {
  defaultCampaignsLazyData: EmailCampaignsLazyData;
  filteredCampaignsLazyData: EmailCampaignsLazyData;
  allCampaigns: AllCampaigns;
  sortingFilter: EmailCampaignSortingFilter;
  statusFilter: EmailCampaignStatusFilter;
  viewType: EmailMarketingCampaignPageType;
  searchTeam: string;
}

export interface FetchEmailCampaignsAjaxParams {
  statusFilter: EmailCampaignStatusFilter;
  sortBy: EmailCampaignSorting;
  offset: number;
  fetchDeleted: boolean;
  viewType: EmailMarketingCampaignPageType;
}

export interface CreatePosterFromEmailMarketingCampaignAjaxParams {
  idEmailMarketingCampaign: string;
  hashedFileName: UserImageHashedFileName;
}

export interface AjaxResponseWithCampaign {
  campaign?: EmailCampaign;
}

export interface DuplicateCampaignResponse {
  emailCampaignId: EmailCampaignId;
  emailTemplateId: string;
}

export interface ChangeScheduleDateTimeParams {
  campaignId: EmailCampaignId;
  newDate: Date;
}

export interface DeleteCampaignParams {
  campaignId: EmailCampaignId;
  deletePermanently?: boolean;
}

export interface PauseAndResumeCampaignHandlers {
  onPauseCampaign: VoidFunction;
  onResumeCampaign: VoidFunction;
}

export interface ToggleCampaignRenameModePayload {
  campaignId: EmailCampaignId;
  isRenaming: boolean;
}

export class AjaxResponseWithUserHashedFileName {}

export enum EmailCampaignReputation {
  EXCELLENT = 'excellent',
  SOLID = 'solid',
  POOR = 'poor',
  CRITICAL = 'critical',
}

export enum EmailCampaignPublishOptionsSublow {
  MAIN_OPTIONS,
  SOCIAL_MEDIA,
  EMAIL_CAMPAIGN,
  WEB_PAGE,
}

export enum EmailCampaignPublishingOptions {
  SOCIAL_MEDIA = 'email_campaign_publish_on_social_media',
  EMAIL = 'email_campaign_publish_on_email',
  WEBPAGE = 'email_campaign_publish_as_web_page',
}

export enum EmailCampaignSocialMediaShareOptions {
  FACEBOOK = 'email_campaign_publish_on_facebook',
  X = 'email_campaign_publish_on_x',
  PINTEREST = 'email_campaign_publish_on_pinterest',
}

export interface SocialShareThumbnailMeta {
  facebookThumb: string;
  xThumb: string;
  pinterestThumb: string;
}

export interface EmailTemplateForEmptyState {
  id: number;
  name: string;
  thumbnailUrl: string;
  previewVideoUrl: string | null;
  customizeUrl: string;
}
